import { TabContext } from '@mui/lab';
import { Box } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';

import { ImplantationInsertion } from '../../../api/characteristics/enumerations';
import {
  CompletionData,
  LotCharacteristicsData,
  SectionStatus,
} from '../../../api/characteristics/types';
import {
  SectionIndexAutocomplete,
  SectionIndexItem,
  scrollToId,
} from '../components/SectionIndexAutocomplete';
import { StyledTab, StyledTabList, StyledTabPanel } from '../styles';
import { LotEnterpriseSection } from './enterprise';
import { LotEvaluatingUnitSection } from './evaluatingUnit';
import { LotLandAndFencingSection } from './landAndFencing';
import { LotRegionSection } from './region';
import { lotSectionIndex } from './sectionIndex';

interface LotCharacteristicsProps {
  characteristicsData: LotCharacteristicsData;
  completionData?: CompletionData;
}

const baseTabs: { key: keyof LotCharacteristicsData; label: string }[] = [
  { key: 'region', label: 'Características e Região' },
  { key: 'evaluating_unit', label: 'Unidade Avaliando' },
  { key: 'land_and_fencing', label: 'Lote e Cercamento' },
];

export function LotCharacteristics({
  characteristicsData,
  completionData,
}: LotCharacteristicsProps): JSX.Element {
  const sectionStatus = (
    key: keyof LotCharacteristicsData
  ): SectionStatus | undefined => completionData?.sections[key]?.status;
  const isCondominium =
    characteristicsData.evaluating_unit.position_and_insertion
      .implantation_insertion === ImplantationInsertion.CONDOMINIUM;

  const tabs = isCondominium
    ? [
        ...baseTabs,
        {
          key: 'enterprise' as keyof LotCharacteristicsData,
          label: 'Empreendimento',
        },
      ]
    : baseTabs;

  const [activeTab, setActiveTab] =
    useState<keyof LotCharacteristicsData>('region');
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (scrollTarget) {
      timeout = setTimeout(() => {
        scrollToId(scrollTarget);
        setScrollTarget(null);
      }, 150);
    }
    return () => clearTimeout(timeout);
  }, [scrollTarget, activeTab]);

  const handleTabChange = (
    _: SyntheticEvent,
    newTab: keyof LotCharacteristicsData
  ): void => {
    setActiveTab(newTab);
  };

  const handleSearchNavigate = (item: SectionIndexItem): void => {
    if (item.tabKey === activeTab) {
      scrollToId(item.sectionId);
      return;
    }
    setActiveTab(item.tabKey as keyof LotCharacteristicsData);
    setScrollTarget(item.sectionId);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <SectionIndexAutocomplete
        options={lotSectionIndex}
        onNavigate={handleSearchNavigate}
      />
      <TabContext value={activeTab}>
        <StyledTabList
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-scrollButtons.Mui-disabled': {
              display: 'none',
            },
          }}
        >
          {tabs.map(({ key, label }) => (
            <StyledTab key={key} value={key} label={label} />
          ))}
        </StyledTabList>
        <StyledTabPanel value={activeTab}>
          {activeTab === 'region' && (
            <LotRegionSection
              formData={characteristicsData.region}
              sectionStatus={sectionStatus('region')}
            />
          )}
          {activeTab === 'evaluating_unit' && (
            <LotEvaluatingUnitSection
              formData={characteristicsData.evaluating_unit}
              sectionStatus={sectionStatus('evaluating_unit')}
            />
          )}
          {activeTab === 'land_and_fencing' && (
            <LotLandAndFencingSection
              formData={characteristicsData.land_and_fencing}
              sectionStatus={sectionStatus('land_and_fencing')}
            />
          )}
          {activeTab === 'enterprise' && characteristicsData.enterprise && (
            <LotEnterpriseSection
              formData={characteristicsData.enterprise}
              sectionStatus={sectionStatus('enterprise')}
            />
          )}
        </StyledTabPanel>
      </TabContext>
    </Box>
  );
}
